'use client';

import { Center, Text, VStack } from '@chakra-ui/react';
import { LoadingIndicator, type LoadingIndicatorSize } from './LoadingIndicator';

export type PageLoaderVariant = 'page' | 'section' | 'inline' | 'card';

interface PageLoaderProps {
  variant?: PageLoaderVariant;
  message?: string;
  submessage?: string;
  minH?: string;
}

/** Use the same spinner within the product's existing loading regions. */
export function PageLoader({ variant = 'page', message, submessage, minH }: PageLoaderProps) {
  const height = {
    page: 'calc(100dvh - 100px)',
    section: '400px',
    card: '200px',
    inline: '60px',
  }[variant];
  const displayMessage = message ?? (variant === 'inline' ? undefined : 'Loading…');

  return (
    <Center
      minH={minH || height}
      w="full"
      role="status"
      aria-label={displayMessage ? undefined : 'Loading'}
      aria-live="polite"
      aria-busy="true"
    >
      <VStack gap="3">
        <LoadingIndicator size={variant === 'inline' ? 'sm' : 'md'} decorative />
        {displayMessage ? (
          <Text fontSize="sm" color="ui.text.secondary">
            {displayMessage}
          </Text>
        ) : null}
        {submessage ? (
          <Text fontSize="xs" color="ui.text.tertiary">
            {submessage}
          </Text>
        ) : null}
      </VStack>
    </Center>
  );
}

export function InlineSpinner({
  size = 'sm',
  label = 'Loading',
  decorative = false,
}: {
  size?: LoadingIndicatorSize;
  label?: string;
  decorative?: boolean;
}) {
  return <LoadingIndicator size={size} label={label} decorative={decorative} />;
}

export function SectionLoader({ message, submessage }: { message?: string; submessage?: string }) {
  return <PageLoader variant="section" message={message} submessage={submessage} />;
}

export function CardLoader({ message }: { message?: string }) {
  return <PageLoader variant="card" message={message} />;
}
